import type { TextInputProps } from '@meterup/atto';
import {
  CompositeField,
  DropdownMenuCheckboxItem,
  FieldContainer,
  FloatingImmediateErrorField,
  Icon,
  MultiComboBox,
  MultiComboBoxItem,
  Select,
  SelectItem,
  styled,
  TextInput,
} from '@meterup/atto';
import { isNumber } from 'lodash-es';
import { type Key, type ReactNode, useCallback, useEffect, useEffectEvent, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import type {
  DateRangeFilter,
  Filter,
  MultiSelectFilter,
  NumberFilter,
  NumberRangeFilter,
  SelectFilter,
  Table,
  TableFilterDefinition,
  TextFilter,
} from '../utils';
import { parseDateTime } from '../../../topics/time';
import {
  DateTimeRangePickerDropdownContents,
  useDateTimeRangePicker,
} from '../../DateTimeRangePicker/DateTimeRangePicker';
import { DateTimeRangeStorageProvider } from '../../DateTimeRangePicker/DateTimeRangeStorageContext';
import { TimePeriod } from '../../DateTimeRangePicker/utils';
import { TableFilterVariant } from '../utils';

type CommonFilterProps = {
  id: string;
  label: string;
};

type TextFilterProps = CommonFilterProps & {
  value: string;
  onChange: (id: string, value: string) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

type SelectFilterProps = CommonFilterProps & {
  value: string;
  options: { value: string; label: ReactNode }[];
  handleFiltersChange: (id: string, key: string) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

type MultiSelectFilterProps = CommonFilterProps & {
  value: string[];
  options: { value: string; label: ReactNode }[];
  handleFiltersChange: (id: string, values: string[]) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

export const TableFiltersFields = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$4',
  padding: '$6 $8',
  width: '100%',
});
TableFiltersFields.displayName = 'TableFiltersFields';

export function TableSelectFilter({
  id,
  label,
  value,
  handleFiltersChange,
  options,
  onKeyDown,
}: SelectFilterProps) {
  if (options) {
    return (
      <FieldContainer background="application">
        <CompositeField
          label={label}
          element={
            <TableFiltersFields>
              <Select
                popoverProps={{ className: `disableOutsideClick_${id}` }}
                aria-label={label}
                value={value}
                onValueChange={(key: string) => handleFiltersChange(id, key)}
                size="medium"
                width="100%"
                onKeyDown={onKeyDown}
              >
                {options.map((option) => (
                  <SelectItem
                    key={option.value}
                    textValue={typeof option.label === 'string' ? option.label : option.value}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </Select>
            </TableFiltersFields>
          }
        />
      </FieldContainer>
    );
  }
}

export function TableMultiSelectFilter({
  id,
  label,
  value,
  handleFiltersChange,
  options,
  onKeyDown,
}: MultiSelectFilterProps) {
  if (options) {
    return (
      <FieldContainer background="application">
        <CompositeField
          label={label}
          element={
            <TableFiltersFields>
              <MultiComboBox
                className={`disableOutsideClick_${id}`}
                onKeyDown={onKeyDown}
                aria-label={label}
                value={value}
                onValueChange={(keys: 'all' | Set<Key>) =>
                  handleFiltersChange(
                    id,
                    keys === 'all' ? [] : Array.from(keys).map((k) => k.toString()),
                  )
                }
                width="100%"
                enableSelectAll
              >
                {options.map((option) => (
                  <MultiComboBoxItem
                    key={option.value}
                    textValue={typeof option.label === 'string' ? option.label : option.value}
                  >
                    {option.label}
                  </MultiComboBoxItem>
                ))}
              </MultiComboBox>
            </TableFiltersFields>
          }
        />
      </FieldContainer>
    );
  }
}

export function TableFiltersDebouncedInput({
  id,
  value,
  label,
  onChange,
  onKeyDown,
  parseValue,
  ...restProps
}: Omit<TextInputProps, 'onChange'> & {
  id: string;
  onChange: (id: string, value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  parseValue?: (value: string) => string;
}) {
  const [localVal, setLocalVal] = useState(value ?? '');
  const debouncedCallback = useDebouncedCallback((val) => {
    onChange(id, val);
  }, 250);

  const handleOnChange = useCallback(
    (val: string) => {
      const parsedValue = parseValue ? parseValue(val) : val;
      setLocalVal(parsedValue);
      debouncedCallback(parsedValue);
    },
    [setLocalVal, debouncedCallback, parseValue],
  );
  return (
    <CompositeField
      label={label}
      element={
        <TableFiltersFields>
          <TextInput
            {...restProps}
            id={id}
            onChange={handleOnChange}
            onKeyDown={onKeyDown}
            value={localVal}
            size="small"
            width="100%"
          />
        </TableFiltersFields>
      }
    />
  );
}

export function TableTextFilter({ id, value, label, onChange, onKeyDown }: TextFilterProps) {
  return (
    <FieldContainer background="application">
      <CompositeField
        label={label}
        element={
          <TableFiltersFields>
            <TableFiltersDebouncedInput
              id={id}
              label={label}
              onChange={onChange}
              onKeyDown={onKeyDown}
              value={value}
            />
          </TableFiltersFields>
        }
      />
    </FieldContainer>
  );
}

const NumberRangeFieldsIcon = styled(Icon, {
  width: '$12',
  height: '$12',
});
NumberRangeFieldsIcon.displayName = 'NumberRangeFieldsIcon';

export function TableNumberFilter({
  id,
  value,
  label,
  handleFiltersChange,
  onKeyDown,
}: {
  id: string;
  value: number | null;
  label: string;
  handleFiltersChange: (id: string, value: number | null) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}) {
  return (
    <FieldContainer background="application">
      <CompositeField
        label={label}
        element={
          <TableFiltersFields>
            <TableFiltersDebouncedInput
              id={id}
              label={label}
              onChange={(inputId, key) => {
                const num = Number(key);
                const val = key === '' ? null : num;
                handleFiltersChange(inputId, val);
              }}
              onKeyDown={onKeyDown}
              value={value === null ? '' : String(value)}
              type="number"
            />
          </TableFiltersFields>
        }
      />
    </FieldContainer>
  );
}

export function TableNumberRangeFilter({
  id: idProp,
  value,
  label,
  handleFiltersChange,
  onKeyDown,
}: {
  id: string;
  value: [number | null, number | null];
  label: string;
  handleFiltersChange: (id: string, value: [number | null, number | null]) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}) {
  const isInvalidRange = isNumber(value[0]) && isNumber(value[1]) && value[0] > value[1];
  return (
    <FieldContainer background="application">
      <CompositeField
        errorMessage={isInvalidRange ? 'Minimum cannot be above the maximum.' : undefined}
        label="Number range"
        element={
          <TableFiltersFields>
            <CompositeField
              label="Min"
              element={
                <TableFiltersDebouncedInput
                  id={`${idProp}-min`}
                  label={`${label} min.`}
                  onChange={(id, key) => {
                    const min = Number(key);
                    const max = value[1];
                    const val = key === '' ? null : min;
                    handleFiltersChange(idProp, [val, max]);
                  }}
                  onKeyDown={onKeyDown}
                  value={String(value[0])}
                  type="number"
                />
              }
            />
            <NumberRangeFieldsIcon icon="arrow-right" />
            <CompositeField
              label="Max"
              element={
                <TableFiltersDebouncedInput
                  id={`${idProp}-max`}
                  label={`${label} max.`}
                  onChange={(id, key) => {
                    const min = value[0];
                    const max = Number(key);
                    const val = key === '' ? null : max;
                    handleFiltersChange(idProp, [min, val]);
                  }}
                  onKeyDown={onKeyDown}
                  value={String(value[1])}
                  type="number"
                />
              }
            />
          </TableFiltersFields>
        }
      />
    </FieldContainer>
  );
}

interface ServerTableMultiSelectFieldProps<R> {
  filter: MultiSelectFilter;
  definition: TableFilterDefinition;
  table: Table<R>;
}

export function ServerTableMultiSelectField<R>({
  filter,
  definition,
  table,
}: ServerTableMultiSelectFieldProps<R>) {
  return definition.options.map((option) => {
    const isSelected = filter.values.includes(option.value);
    return (
      <DropdownMenuCheckboxItem
        key={option.value}
        closeOnClick={false}
        onCheckedChange={(checked) => {
          const newValue = checked
            ? [...filter.values, option.value]
            : filter.values.filter((v) => v !== option.value);
          table.updateFilter({
            key: filter.key,
            type: TableFilterVariant.MultiSelect,
            values: newValue.sort(),
          });
        }}
        checked={isSelected}
        disabled={option.disabled}
        textValue={option.value}
      >
        {option.label ?? option.value}
      </DropdownMenuCheckboxItem>
    );
  });
}

interface ServerTableNumberRangeFieldProps<R> {
  filter: NumberRangeFilter;
  definition: TableFilterDefinition;
  table: Table<R>;
}

export function ServerTableNumberRangeField<R>({
  filter,
  definition,
  table,
}: ServerTableNumberRangeFieldProps<R>) {
  const isInvalidRange =
    isNumber(filter.start) && isNumber(filter.end) && filter.start > filter.end;
  return (
    <FieldContainer background="application">
      <CompositeField
        errorMessage={isInvalidRange ? 'Minimum cannot be above the maximum.' : undefined}
        label="Number range"
        element={
          <TableFiltersFields>
            <CompositeField
              label="Min"
              element={
                <TableFiltersDebouncedInput
                  id={`${definition.id}-min`}
                  label={`${definition.label} min.`}
                  onChange={(_, value) => {
                    let min: number | null = parseInt(value, 10);
                    if (Number.isNaN(min)) {
                      min = null;
                    }
                    table.updateFilter({
                      key: filter.key,
                      type: TableFilterVariant.NumberRange,
                      start: min,
                      end: filter.end,
                    });
                  }}
                  value={filter.start?.toString() ?? ''}
                  type="number"
                />
              }
            />
            <NumberRangeFieldsIcon icon="arrow-right" />
            <CompositeField
              label="Max"
              element={
                <TableFiltersDebouncedInput
                  id={`${definition.id}-min`}
                  label={`${definition.label} min.`}
                  onChange={(_, value) => {
                    let max: number | null = parseInt(value, 10);
                    if (Number.isNaN(max)) {
                      max = null;
                    }
                    table.updateFilter({
                      key: filter.key,
                      type: TableFilterVariant.NumberRange,
                      start: filter.start,
                      end: max,
                    });
                  }}
                  value={filter.start?.toString() ?? ''}
                  type="number"
                />
              }
            />
          </TableFiltersFields>
        }
      />
    </FieldContainer>
  );
}

interface ServerTableSelectFieldProps<R> {
  filter: SelectFilter;
  definition: TableFilterDefinition;
  table: Table<R>;
}

export function ServerTableSelectField<R>({
  filter,
  definition,
  table,
}: ServerTableSelectFieldProps<R>) {
  return (
    <FieldContainer background="application">
      <CompositeField
        label={definition.label}
        element={
          <TableFiltersFields>
            <Select
              popoverProps={{ className: `disableOutsideClick_${definition.id}` }}
              aria-label={definition.label}
              value={filter.value}
              onValueChange={(value: string) =>
                table.updateFilter({
                  key: filter.key,
                  type: TableFilterVariant.Select,
                  value,
                })
              }
              size="medium"
              width="100%"
            >
              {definition.options.map((option) => (
                <SelectItem
                  key={option.value}
                  textValue={typeof option.label === 'string' ? option.label : option.value}
                >
                  {option.label}
                </SelectItem>
              ))}
            </Select>
          </TableFiltersFields>
        }
      />
    </FieldContainer>
  );
}

interface ServerTableDateRangeFieldProps<R> {
  filter: DateRangeFilter;
  definition: TableFilterDefinition;
  table: Table<R>;
}

export function ServerTableDateRangeField<R>({
  filter,
  definition,
  table,
}: ServerTableDateRangeFieldProps<R>) {
  const hasValue = !!filter.start && !!filter.end;

  const state = useDateTimeRangePicker({
    fixAnchorToNow: false,
    allowCustom: true,
    defaultValue: hasValue ? TimePeriod.Custom : TimePeriod.OneDay,
    defaultStart: filter.start ?? undefined,
    defaultEnd: filter.end ?? undefined,
  });

  const handleChange = useEffectEvent((value: [string | null, string | null]) => {
    table.updateFilter({
      key: filter.key,
      type: TableFilterVariant.DateRange,
      start: parseDateTime(value[0]),
      end: parseDateTime(value[1]),
    });
  });

  useEffect(() => {
    handleChange([state.startDate, state.endDate]);
  }, [state.startDate, state.endDate]);

  return (
    <DateTimeRangeStorageProvider>
      <FloatingImmediateErrorField
        label={definition.label}
        element={
          <DateTimeRangePickerDropdownContents
            state={state}
            options={[
              TimePeriod.ThirtyMinutes,
              TimePeriod.OneHour,
              TimePeriod.TwoHours,
              TimePeriod.ThreeHours,
              TimePeriod.SixHours,
              TimePeriod.TwelveHours,
              TimePeriod.OneDay,
              TimePeriod.SevenDays,
              TimePeriod.ThirtyDays,
            ]}
          />
        }
      />
    </DateTimeRangeStorageProvider>
  );
}

interface ServerTableNumberFieldProps<R> {
  filter: NumberFilter;
  definition: TableFilterDefinition;
  table: Table<R>;
}

export function ServerTableNumberField<R>({
  filter,
  definition,
  table,
}: ServerTableNumberFieldProps<R>) {
  return (
    <FieldContainer background="application">
      <CompositeField
        label={definition.label}
        element={
          <TableFiltersFields>
            <TableFiltersDebouncedInput
              id={definition.id}
              label={definition.label}
              onChange={(_, value) => {
                const n = parseInt(value, 10);
                if (Number.isNaN(n)) {
                  return;
                }
                table.updateFilter({
                  key: filter.key,
                  type: TableFilterVariant.Number,
                  value: n,
                });
              }}
              value={filter.value.toString(10)}
              type="number"
            />
          </TableFiltersFields>
        }
      />
    </FieldContainer>
  );
}

interface ServerTableTextFieldProps<R> {
  filter: TextFilter;
  definition: TableFilterDefinition;
  table: Table<R>;
}

export function ServerTableTextField<R>({
  filter,
  definition,
  table,
}: ServerTableTextFieldProps<R>) {
  return (
    <FieldContainer background="application">
      <CompositeField
        label={definition.label}
        element={
          <TableFiltersFields>
            <TableFiltersDebouncedInput
              id={definition.id}
              label={definition.label}
              onChange={(_, value) => {
                table.updateFilter({
                  key: filter.key,
                  type: TableFilterVariant.Text,
                  value,
                });
              }}
              value={filter.value}
            />
          </TableFiltersFields>
        }
      />
    </FieldContainer>
  );
}

interface ServerTableFiltersFieldProps<R> {
  filter: Filter;
  definition: TableFilterDefinition;
  table: Table<R>;
}

export function ServerTableFiltersField<R>({
  filter,
  definition,
  table,
}: ServerTableFiltersFieldProps<R>) {
  switch (filter.type) {
    case TableFilterVariant.Bool:
      return null;
    case TableFilterVariant.DateRange:
      return <ServerTableDateRangeField filter={filter} table={table} definition={definition} />;
    case TableFilterVariant.MultiSelect:
      return <ServerTableMultiSelectField filter={filter} table={table} definition={definition} />;
    case TableFilterVariant.Number:
      return <ServerTableNumberField filter={filter} table={table} definition={definition} />;
    case TableFilterVariant.NumberRange:
      return <ServerTableNumberRangeField filter={filter} table={table} definition={definition} />;
    case TableFilterVariant.Select:
      return <ServerTableSelectField filter={filter} table={table} definition={definition} />;
    case TableFilterVariant.Text:
      return <ServerTableTextField filter={filter} table={table} definition={definition} />;
  }
}
